import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import classNames from 'classnames';

import Button from 'CommonComponents/Button';
import TextButton from 'CommonComponents/TextButton';

import XMarkIcon from 'Assets/icons/xmark.svg';

const NOTE_MAX_LENGTH = 500;
const GAP = 8;
const VIEWPORT_MARGIN = 8;
const ARROW_SIZE = 12;

// React events bubble through the component tree even across a portal, so
// without this every keystroke and click in the note would also reach the day
// cell — Space would be swallowed and Enter would open the time editor.
const stop = (event) => event.stopPropagation();

/**
 * The note on one day. Rendered into the body at a fixed position so the
 * table's scroll container can never clip it, below its button when there is
 * room and above it when there is not.
 *
 * Nothing saves until Save note or Cmd/Ctrl + Enter; every other way out
 * discards the edit. Scrolling or resizing closes it too, since a fixed box
 * left behind would point at a cell that has moved.
 *
 * `onSave` must resolve to `true` when the write landed. A note save is a full
 * entry write and carries every refusal one can produce — a lock set since the
 * page loaded, a rate card gap — so on anything else the box stays open with
 * the text intact; the parent has already said why.
 *
 * `onClose(restoreFocus)` is told whether focus should go back to the note
 * button: yes when the user dismissed the box from inside it, no when they
 * clicked or scrolled somewhere else.
 */
export default function TimeNotePopover({ anchorRef, note, context, onSave, onClose }) {
    const [value, setValue] = useState(note ?? '');
    const [saving, setSaving] = useState(false);
    const [position, setPosition] = useState(null);
    const rootRef = useRef(null);
    const textareaRef = useRef(null);
    const mountedRef = useRef(true);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        [],
    );

    useLayoutEffect(() => {
        const anchor = anchorRef.current?.getBoundingClientRect();
        const box = rootRef.current?.getBoundingClientRect();

        if (!anchor || !box) {
            return;
        }

        const fitsBelow = anchor.bottom + GAP + box.height <= window.innerHeight;
        const fitsAbove = anchor.top - GAP - box.height >= VIEWPORT_MARGIN;
        const anchorCentre = anchor.left + anchor.width / 2;
        const left = Math.min(Math.max(VIEWPORT_MARGIN, anchorCentre - box.width / 2), window.innerWidth - box.width - VIEWPORT_MARGIN);

        // With room on neither side the box is pinned to the top of the
        // viewport, so its header and close button stay reachable; it then
        // points at nothing, so it has no arrow.
        let top = VIEWPORT_MARGIN;
        let side = null;

        if (fitsBelow) {
            top = anchor.bottom + GAP;
            side = 'below';
        } else if (fitsAbove) {
            top = anchor.top - box.height - GAP;
            side = 'above';
        }

        setPosition({
            side,
            left,
            top,
            arrowLeft: Math.min(Math.max(ARROW_SIZE, anchorCentre - left - ARROW_SIZE / 2), box.width - ARROW_SIZE * 2),
        });
    }, [anchorRef]);

    // Once placed rather than on mount: the box is invisible until measured,
    // and an invisible element cannot take focus.
    const placed = !!position;

    // The caret goes after the existing text, so typing adds to the note
    // rather than in front of it.
    useEffect(() => {
        const textarea = textareaRef.current;

        if (placed && textarea) {
            textarea.focus({ preventScroll: true });
            textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        }
    }, [placed]);

    useEffect(() => {
        // A click or focus landing anywhere else dismisses the box. Focus is
        // watched where it lands rather than where it left: Tab past Save note
        // leaves the page for the browser's own controls before wrapping back
        // in, and that blur names no next element. The note button is exempt
        // so clicking it can toggle the box shut instead of closing and
        // reopening it.
        const onOutside = (event) => {
            if (!rootRef.current?.contains(event.target) && !anchorRef.current?.contains(event.target)) {
                onCloseRef.current(false);
            }
        };

        // Captured so a scroll on the table wrapper or any other ancestor
        // counts, not just the window. The textarea's own scrolling does not.
        const onScroll = (event) => {
            if (!rootRef.current?.contains(event.target)) {
                onCloseRef.current(false);
            }
        };

        const onResize = () => onCloseRef.current(false);

        document.addEventListener('mousedown', onOutside);
        document.addEventListener('focusin', onOutside);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);

        return () => {
            document.removeEventListener('mousedown', onOutside);
            document.removeEventListener('focusin', onOutside);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [anchorRef]);

    const save = async () => {
        if (saving) {
            return;
        }

        const trimmed = value.trim();

        setSaving(true);

        // An emptied box removes the note.
        const saved = await onSave(trimmed === '' ? null : trimmed);

        if (!mountedRef.current) {
            return;
        }

        setSaving(false);

        if (saved === true) {
            onClose(true);
        }
    };

    const handleKeyDown = (event) => {
        stop(event);

        if (event.key === 'Escape') {
            event.preventDefault();
            onClose(true);
        } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            save();
        }
    };

    return ReactDOM.createPortal(
        <div
            ref={rootRef}
            role="dialog"
            aria-label={$t('Note')}
            className={classNames(
                'tw-fixed tw-z-[1050] tw-w-[300px] tw-rounded-xl tw-bg-white tw-p-3 tw-text-start tw-shadow-lg tw-ring-1 tw-ring-charcoal-15',
                {
                    'tw-invisible': !position,
                },
            )}
            style={position ? { top: position.top, left: position.left } : { top: 0, left: 0 }}
            onKeyDown={handleKeyDown}
            onKeyUp={stop}
            onKeyPress={stop}
            onClick={stop}
            onMouseDown={stop}
            onPointerDown={stop}
        >
            {!!position?.side && (
                <span
                    aria-hidden="true"
                    className={classNames('tw-absolute tw-h-3 tw-w-3 tw-rotate-45 tw-bg-white', {
                        '-tw-top-1.5': position.side === 'below',
                        '-tw-bottom-1.5': position.side === 'above',
                    })}
                    style={{ left: position.arrowLeft }}
                />
            )}

            <div className="tw-flex tw-items-start tw-justify-between tw-mb-2">
                <div className="tw-flex tw-flex-col">
                    <span className="tw-text-sm tw-font-semibold tw-text-charcoal">{$t('Note')}</span>
                    {!!context && <span className="tw-text-xs tw-text-charcoal-50">{context}</span>}
                </div>
                <TextButton variant="primary" onClick={() => onClose(true)} aria-label={$t('Close')}>
                    <XMarkIcon className="tw-h-3 tw-w-3" />
                </TextButton>
            </div>

            <textarea
                ref={textareaRef}
                rows={4}
                maxLength={NOTE_MAX_LENGTH}
                value={value}
                placeholder={$t('What did you work on?')}
                onChange={(event) => setValue(event.target.value)}
                className="tw-w-full tw-resize-none tw-rounded-lg tw-border tw-border-charcoal-25 tw-px-2 tw-py-1 tw-text-sm tw-text-charcoal tw-outline-none focus:tw-border-blue"
            />

            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mt-2">
                <span className="tw-text-xs tw-text-charcoal-50">{$t('Esc to close · ⌘/Ctrl + Enter to save')}</span>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <Button variant="secondary" small onClick={() => onClose(true)}>
                        {$t('Cancel')}
                    </Button>
                    <Button variant="primary" small loading={saving} disabled={saving} onClick={save}>
                        {$t('Save note')}
                    </Button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
